// File: src/pages/CategoryProducts.jsx - COMPLETE with SEO
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { productsAPI, categoriesAPI, seoAPI } from '../services/api';
import SEO from '../components/SEO';
import { SlidersHorizontal, X, ChevronDown, Grid, List } from 'lucide-react';
import './css/CategoryProducts.css';

const CategoryProducts = ({ openCart }) => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(null);
  const [seoData, setSeoData] = useState(null);
  
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [inStock, setInStock] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const categoryResponse = await categoriesAPI.getBySlug(slug);
        setCategory(categoryResponse.data);

        const productsResponse = await productsAPI.getAll(categoryResponse.data.id);
        setProducts(productsResponse.data);
        setFilteredProducts(productsResponse.data);

        try {
          const { data } = await seoAPI.getCategoryMetaBySlug(slug);
          setSeoData(data);
        } catch (seoErr) {
          console.error('SEO fetch error:', seoErr);
        }

      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.response?.data?.message || err.message || 'Impossible de charger les donnees');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchData();
    }
  }, [slug]);

  useEffect(() => {
    applyFilters();
  }, [products, sortBy, priceRange, inStock]);

  const decoratedProducts = useMemo(() => (
    filteredProducts.map((product) => ({
      ...product,
      categoryName: category?.name
    }))
  ), [filteredProducts, category?.name]);

  const applyFilters = () => {
    let filtered = [...products];

    if (inStock) {
      filtered = filtered.filter(product => product.stock > 0);
    }

    if (priceRange.min !== '') {
      filtered = filtered.filter(product => {
        const price = product.salePrice !== null ? product.salePrice : product.regularPrice;
        return price >= parseFloat(priceRange.min);
      });
    }
    if (priceRange.max !== '') {
      filtered = filtered.filter(product => {
        const price = product.salePrice !== null ? product.salePrice : product.regularPrice;
        return price <= parseFloat(priceRange.max);
      });
    }

    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => {
          const priceA = a.salePrice !== null ? a.salePrice : a.regularPrice;
          const priceB = b.salePrice !== null ? b.salePrice : b.regularPrice;
          return priceA - priceB;
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const priceA = a.salePrice !== null ? a.salePrice : a.regularPrice;
          const priceB = b.salePrice !== null ? b.salePrice : b.regularPrice;
          return priceB - priceA;
        });
        break;
      case 'name-az':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-za':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'newest':
        filtered.sort((a, b) => b.id - a.id);
        break;
      default:
        break;
    }

    setFilteredProducts(filtered);
  };

  const clearFilters = () => {
    setSortBy('default');
    setPriceRange({ min: '', max: '' });
    setInStock(false);
  };

  const hasActiveFilters = () => {
    return sortBy !== 'default' || priceRange.min !== '' || priceRange.max !== '' || inStock;
  };

  if (loading) {
    return (
      <div className="cat-loading">
        <div className="cat-spinner"></div>
        <p>Chargement de la categorie...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cat-error">
        <h2>Erreur lors du chargement de la categorie</h2>
        <p>{error}</p>
        <p>Veuillez verifier que le serveur backend est en cours d'execution et accessible.</p>
      </div>
    );
  }

  return (
    <>
      {seoData ? (
        <SEO
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          image={seoData.image}
          url={`/category/${category?.slug || slug}`}
          category={category}
          structuredData={seoData.structuredData}
          breadcrumbs={[
            { name: 'Accueil', url: '/' },
            { name: category?.name || 'Catégorie', url: `/category/${category?.slug || slug}` }
          ]}
        />
      ) : category ? (
        <SEO
          title={`${category.name} - Acheter en ligne | Reyza`}
          description={category.description || `Decouvrez notre collection ${category.name}. Meilleurs prix et livraison gratuite.`}
          image={category.imageUrl}
          url={`/category/${category.slug || slug}`}
          category={category}
          breadcrumbs={[
            { name: 'Accueil', url: '/' },
            { name: category.name, url: `/category/${category.slug || slug}` }
          ]}
        />
      ) : null}

      <div className="cat-container">
        {category && (
          <div className="cat-hero" style={{
            backgroundImage: category.imageUrl 
              ? `linear-gradient(135deg, rgba(38, 38, 37, 0.92) 0%, rgba(0, 0, 0, 0.88) 100%), url(${category.imageUrl})`
              : 'linear-gradient(135deg, #262625 0%, #000000 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}>
            <div className="cat-hero-content">
              <div className="cat-hero-text">
                <h1 className="cat-hero-title">{category.name}</h1>
                {category.description && (
                  <p className="cat-hero-description">{category.description}</p>
                )}
                <div className="cat-hero-stats">
                  <span className="cat-hero-stat">
                    {filteredProducts.length} {filteredProducts.length === 1 ? 'Produit' : 'Produits'}
                  </span>
                  {filteredProducts.length !== products.length && (
                    <span className="cat-hero-stat-filtered">
                      (Filtre sur {products.length})
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="cat-toolbar">
          <div className="cat-toolbar-left">
            <button 
              className={`cat-filter-toggle ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal size={18} />
              <span>Filtres</span>
              {hasActiveFilters() && <span className="cat-filter-badge"></span>}
            </button>

            <div className="cat-view-toggle">
              <button
                className={`cat-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Vue grille"
              >
                <Grid size={18} />
              </button>
              <button
                className={`cat-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="Vue liste"
              >
                <List size={18} />
              </button>
            </div>
          </div>

          <div className="cat-toolbar-right">
            <div className="cat-sort-wrapper">
            <label htmlFor="sort-select" className="cat-sort-label">Trier par :</label>
              <div className="cat-select-wrapper">
                <select
                  id="sort-select"
                  className="cat-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="default">Par defaut</option>
                  <option value="price-low">Prix : du plus bas au plus haut</option>
                  <option value="price-high">Prix : du plus haut au plus bas</option>
                  <option value="name-az">Nom : A a Z</option>
                  <option value="name-za">Nom : Z a A</option>
                  <option value="newest">Plus recents</option>
                </select>
                <ChevronDown size={16} className="cat-select-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="cat-main">
          <aside className={`cat-filters ${showFilters ? 'show' : ''}`}>
            <div className="cat-filters-header">
              <h3 className="cat-filters-title">Filtres</h3>
              <button 
                className="cat-filters-close"
                onClick={() => setShowFilters(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="cat-filters-content">
              <div className="cat-filter-group">
                <h4 className="cat-filter-title">Plage de prix</h4>
                <div className="cat-price-inputs">
                  <div className="cat-price-input-wrapper">
                    <label className="cat-price-label">Min</label>
                    <input
                      type="number"
                      placeholder="0"
                      className="cat-price-input"
                      value={priceRange.min}
                      onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div className="cat-price-input-wrapper">
                    <label className="cat-price-label">Max</label>
                    <input
                      type="number"
                      placeholder="Sans limite"
                      className="cat-price-input"
                      value={priceRange.max}
                      onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div className="cat-filter-group">
                <h4 className="cat-filter-title">Disponibilite</h4>
                <label className="cat-checkbox-label">
                  <input
                    type="checkbox"
                    className="cat-checkbox"
                    checked={inStock}
                    onChange={(e) => setInStock(e.target.checked)}
                  />
                  <span className="cat-checkbox-text">En stock uniquement</span>
                </label>
              </div>

              {hasActiveFilters() && (
                <button 
                  className="cat-clear-filters"
                  onClick={clearFilters}
                >
                  Effacer tous les filtres
                </button>
              )}
            </div>
          </aside>

          <div className="cat-products-section">
            {filteredProducts.length === 0 ? (
              <div className="cat-no-products">
              <p className="cat-no-products-title">Aucun produit trouve</p>
              <p className="cat-no-products-text">
                {hasActiveFilters() 
                  ? 'Essayez d\'ajuster vos filtres pour voir plus de resultats.'
                  : 'Revenez bientot pour les nouveautes !'}
              </p>
                {hasActiveFilters() && (
                  <button 
                    className="cat-clear-filters-btn"
                    onClick={clearFilters}
                  >
                  Effacer les filtres
                  </button>
                )}
              </div>
            ) : (
              <div className={`cat-product-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
                {decoratedProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    openCart={openCart}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {showFilters && (
          <div 
            className="cat-filters-overlay"
            onClick={() => setShowFilters(false)}
          ></div>
        )}
      </div>
    </>
  );
};

export default CategoryProducts;
